// Stage 14I — pure view-model helpers for the unified Current Read.
export const MAX_HYPOTHESES = 3;
export const MAX_ALTERNATIVES = 4;

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

export function activeHypotheses(read) {
  return (read?.active_hypotheses || []).slice(0, MAX_HYPOTHESES);
}

export function testingKeys(read) {
  return new Set(activeHypotheses(read).flatMap((h) => h.related_concepts || []));
}

export function leadParagraph(read) {
  return read?.big_picture?.paragraphs?.[0] || null;
}

function isTesting(keys, testing) {
  return (keys || []).some((k) => testing.has(k));
}

// Strongest non-testing inferences, de-duplicated, strongest first.
export function learnedItems(read) {
  const testing = testingKeys(read);
  const lead = leadParagraph(read);
  const items = [];
  for (const p of (read?.big_picture?.paragraphs || []).slice(1)) {
    if (isTesting(p.insight_keys, testing)) continue;
    items.push({
      id: "p-" + p.theme, title: p.title, text: p.text, confidence: p.confidence,
      lines: p.evidence_lines, answer_basis: p.answer_basis, learning_note: p.learning_note,
      surprise: p.surprise, stated: p.stated, what_changed: p.what_changed,
    });
  }
  for (const s of read?.sections || []) {
    for (const c of s.insights || []) {
      if (isTesting(["obs:" + c.insight_id], testing)) continue;
      items.push({
        id: c.insight_id, title: c.title, text: c.body, confidence: c.confidence,
        confidence_label: c.confidence_label, evidence: c.evidence, lines: c.evidence_summary,
        answer_basis: c.answer_basis, learning_note: c.learning_note, surprise: s.key === "surprises" || c.tension,
        observation_id: c.insight_id, student_signal: c.student_signal,
      });
    }
  }
  const seen = new Set(lead ? [norm(lead.title)] : []);
  return items
    .filter((i) => {
      const k = norm(i.title);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
}

export function alternatives(read) {
  return (read?.alternatives || []).slice(0, MAX_ALTERNATIVES);
}

export function hasWins(read) {
  return Boolean(read?.what_wins?.relations?.length);
}

export function hypothesisResponsePath(id) {
  return "/realisations/hypotheses/" + encodeURIComponent(id) + "/response";
}

export function confidenceChange(before, after) {
  if (!before || !after || before === after) return null;
  return before + "% → " + after + "%";
}

// Stage 14J — why an answer matters, and what an alternative is for.

export function decidesBetween(h) {
  const c = (h?.competing_explanations || []).filter(Boolean);
  if (c.length < 2) return null;
  const lower = (s) => s.charAt(0).toLowerCase() + s.slice(1);
  return "This answer would help us decide between: " + lower(c[0]) + ", or " + lower(c[1]) + ".";
}

export function alternativeDetails(a) {
  return [
    ["different", "Why this is different", a?.why_this_is_different],
    ["tests", "What this tests", a?.what_this_tests],
  ].filter(([, , text]) => text).map(([key, label, text]) => ({ key, label, text }));
}

// Stage 14K — one focused question at a time, and natural/spoken answers.

export function primaryQuestion(read) {
  return activeHypotheses(read)[0] || null;
}

export function otherQuestions(read) {
  return activeHypotheses(read).slice(1);
}

export function speechSupported() {
  return typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

// Stage 14L — open, editorial layout: portrait prose, revised passages, open vs answered suggestions.

export function portraitParagraphs(read) {
  const testing = testingKeys(read);
  return (read?.big_picture?.paragraphs || []).slice(1).filter((p) => !isTesting(p.insight_keys, testing));
}

export function insightItems(read) {
  return learnedItems(read).filter((i) => !String(i.id).startsWith("p-"));
}

// Split prose so the passages an answer just changed can be highlighted in place.
export function revisedSegments(text, passages) {
  const marks = (passages || []).filter((p) => p && String(text || "").includes(p));
  if (!marks.length) return [{ text: text || "", revised: false }];
  const out = [];
  let rest = String(text);
  for (const m of marks) {
    const i = rest.indexOf(m);
    if (i < 0) continue;
    if (i > 0) out.push({ text: rest.slice(0, i), revised: false });
    out.push({ text: m, revised: true });
    rest = rest.slice(i + m.length);
  }
  if (rest) out.push({ text: rest, revised: false });
  return out;
}

export function openAlternatives(read) {
  return alternatives({ alternatives: (read?.alternatives || []).filter((a) => !a.response || a.draft) });
}

export function answeredAlternatives(read) {
  return (read?.alternatives || []).filter((a) => a.response && !a.draft);
}
